import React, { useCallback, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { apiPost } from '../../lib/api';
import TurnstileWidget, { isTurnstileEnabled } from '../security/TurnstileWidget';

const ContactFormCompact: React.FC = () => {
  const { t } = useTranslation();
  const [formState, setFormState] = useState({
    name: '',
    organization: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    website: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const formStartedAt = useRef(Date.now());

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isTurnstileEnabled && !turnstileToken) {
      setError('Please complete the verification first.');
      return;
    }
    setIsSubmitting(true);
    setError('');
    try {
      await apiPost('/api/public/contact', {
        ...formState,
        form_started_at: formStartedAt.current,
        turnstile_token: turnstileToken,
      });
      setIsSuccess(true);
      setFormState({ name: '', organization: '', email: '', phone: '', subject: '', message: '', website: '' });
      setTurnstileToken('');
      formStartedAt.current = Date.now();
    } catch (err: any) {
      setError(err.message || 'Could not submit your inquiry.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormState((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleVerify = useCallback((token: string) => {
    setTurnstileToken(token);
  }, []);

  const clearVerification = useCallback(() => {
    setTurnstileToken('');
  }, []);

  if (isSuccess) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-lg border border-[#FFD700]/30 bg-white/5 p-8 text-center"
      >
        <h3 className="mt-4 text-2xl font-extrabold text-white">{t('contact.form.success.title')}</h3>
        <p className="mt-2 text-white/70">{t('contact.form.success.message')}</p>
        <button
          onClick={() => setIsSuccess(false)}
          className="mt-6 inline-flex rounded-lg bg-[#FFD700] px-5 py-2.5 font-bold text-black transition-opacity hover:opacity-90"
        >
          {t('contact.form.success.another')}
        </button>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.45, ease: 'easeOut' }}
      className="rounded-lg border border-white/10 bg-black/30 p-7 backdrop-blur md:p-8"
    >
      <form onSubmit={handleSubmit}>
        <input
          tabIndex={-1}
          autoComplete="off"
          name="website"
          value={formState.website}
          onChange={handleChange}
          className="hidden"
          aria-hidden="true"
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-extrabold uppercase tracking-[0.16em] text-white/60">
              {t('contact.form.name')}
            </label>
            <input
              required
              name="name"
              value={formState.name}
              onChange={handleChange}
              className="mt-2 w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#FFD700]/40"
              placeholder="e.g., Jane Doe"
            />
          </div>
          <div>
            <label className="text-xs font-extrabold uppercase tracking-[0.16em] text-white/60">
              {t('contact.form.organization')}
            </label>
            <input
              name="organization"
              value={formState.organization}
              onChange={handleChange}
              className="mt-2 w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#FFD700]/40"
              placeholder="Organisation (optional)"
            />
          </div>
          <div>
            <label className="text-xs font-extrabold uppercase tracking-[0.16em] text-white/60">
              {t('contact.form.email')}
            </label>
            <input
              required
              type="email"
              name="email"
              value={formState.email}
              onChange={handleChange}
              className="mt-2 w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#FFD700]/40"
              placeholder="you@organisation.com"
            />
          </div>
          <div>
            <label className="text-xs font-extrabold uppercase tracking-[0.16em] text-white/60">
              {t('contact.form.phone')}
            </label>
            <input
              type="tel"
              name="phone"
              value={formState.phone}
              onChange={handleChange}
              className="mt-2 w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#FFD700]/40"
              placeholder="+250 7xx xxx xxx"
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-extrabold uppercase tracking-[0.16em] text-white/60">
              {t('contact.form.subject.label')}
            </label>
            <select
              required
              name="subject"
              value={formState.subject}
              onChange={handleChange}
              className="mt-2 w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#FFD700]/40"
            >
              <option value="" disabled>
                Select…
              </option>
              <option value="General Inquiry">{t('contact.form.subject.general')}</option>
              <option value="Buy Produce">{t('contact.form.subject.buy')}</option>
              <option value="Partnership">{t('contact.form.subject.partnership')}</option>
              <option value="Other">{t('contact.form.subject.other')}</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-extrabold uppercase tracking-[0.16em] text-white/60">
              {t('contact.form.message')}
            </label>
            <textarea
              required
              name="message"
              value={formState.message}
              onChange={handleChange}
              rows={5}
              className="mt-2 w-full resize-none rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#FFD700]/40"
              placeholder="Tell us what you need, where you're located, and your timeline."
            />
          </div>
        </div>

        <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <TurnstileWidget action="contact" onVerify={handleVerify} onExpire={clearVerification} />
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex justify-center rounded-lg bg-[#FFD700] px-6 py-3 font-extrabold text-black transition-opacity hover:opacity-90 disabled:opacity-60"
          >
            {isSubmitting ? t('contact.form.sending') : t('contact.form.submit')}
          </button>
        </div>
      </form>
      {error && <p className="mt-4 text-sm text-red-300">{error}</p>}
    </motion.div>
  );
};

export default ContactFormCompact;
